// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import { PROJECT_WORKBOOK_VERSION } from './CaseBuddyAppConstants.js';
import {
  createEmptyWayFinderHandoffState,
  createWayFinderHandoffStateForStorage
} from './CaseBuddyWayFinderHandoff.js';
import { CASE_DELIVERABLE_GROUPS, CASE_WORKFLOW_GROUPS } from './CaseBuddyWorkflowDefinitions.js';

export function normalizeBearerToken(value) {
  const text = value == null ? '' : String(value).trim();
  if (!text) return '';
  return text.replace(/^Bearer\s+/i, '').trim();
}

export function normalizeAuthSession(session = {}) {
  return {
    isAuthenticated: !!session.isAuthenticated,
    role: session.role ?? null,
    token: normalizeBearerToken(session.token || session.authToken || session.bearerToken || ''),
    accessToken: normalizeBearerToken(session.accessToken || session.access_token || session.idToken || session.id_token || ''),
    jwt: normalizeBearerToken(session.jwt || session.jwtToken || ''),
    canUseNormalMode: !!session.canUseNormalMode,
    canUseAllTools: !!session.canUseAllTools,
    canUseCaseBuddy: !!session.canUseCaseBuddy,
    canUseHermesPage: !!session.canUseHermesPage,
    isAgentAccount: !!session.isAgentAccount,
    agentAccessEnabled: !!session.agentAccessEnabled
  };
}

export function createProjectFileId() {
  return `project-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function createWorkbookSheetId() {
  return `sheet-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function createBlankWorkbookState() {
  const sheetId = createWorkbookSheetId();
  return {
    version: PROJECT_WORKBOOK_VERSION,
    activeSheetId: sheetId,
    sheets: [
      {
        id: sheetId,
        name: 'Sheet 1',
        state: null
      }
    ]
  };
}

export const CASE_SUPPORT_STATE_VERSION = 1;

export const CASE_MAIN_SHEET_NAME = 'Main Data';

export const CASE_RAW_SHEET_NAME = 'Raw Data';

export const CASE_DEFAULT_ROWS = 100;

export const CASE_DEFAULT_COLS = 26;

export const CASE_COLUMN_HEADERS = Object.freeze([
  'Path Label',
  'Transaction Hash',
  'From Address',
  'To Address',
  'UTC Date',
  'Amount',
  'TokenSymbol',
  '',
  '',
  'System Notes',
  'Attribution',
  'Analyst Notes',
  'Funds Followed',
  'Prior Balance',
  'From Hash',
  '',
  'Continues As',
  'Left Bounds Display',
  'Left Bounds Numeric',
  'Chain Id',
  'Chain Name',
  'Hop Logic'
]);

export const CASE_NAV_ITEMS = Object.freeze([
  { id: 'case-overview', icon: '◎', label: 'Case overview', shortLabel: 'Case' },
  { id: 'case-input', icon: '▦', label: 'Input, main, and raw sheets', shortLabel: 'Input' },
  { id: 'case-output', icon: '⇄', label: 'Output review buffer', shortLabel: 'Output' },
  { id: 'case-files', icon: '▤', label: 'Case file manager', shortLabel: 'Files' },
  { id: 'case-workflow', icon: '✓', label: 'Workflow and tools', shortLabel: 'Flow' },
  { id: 'case-deliverables', icon: '↗', label: 'Deliverables', shortLabel: 'Ship' },
  { id: 'case-instructions', icon: '?', label: 'How to use Case Buddy Web App', shortLabel: 'Help' }
]);

export function createEmptyGrid(rows = CASE_DEFAULT_ROWS, cols = CASE_DEFAULT_COLS) {
  return Array.from({ length: rows }, () => Array(cols).fill(''));
}

export function normalizeCaseMatrix(data, minRows = CASE_DEFAULT_ROWS, minCols = CASE_DEFAULT_COLS) {
  const source = Array.isArray(data) ? data : [];
  const rowCount = Math.max(minRows, source.length);
  const colCount = Math.max(minCols, ...source.map(row => Array.isArray(row) ? row.length : 0));

  return Array.from({ length: rowCount }, (_, rowIndex) => {
    const row = Array.isArray(source[rowIndex]) ? source[rowIndex].slice(0, colCount) : [];
    return row.length >= colCount ? row : [...row, ...Array(colCount - row.length).fill('')];
  });
}

export function createCaseSheetState({ includeHeaders = true } = {}) {
  const data = createEmptyGrid();
  if (includeHeaders) {
    CASE_COLUMN_HEADERS.forEach((header, index) => {
      data[0][index] = header;
    });
  }

  return {
    data,
    colWidths: [],
    rowHeights: [],
    frozenTop: includeHeaders ? 1 : 0,
    frozenLeft: 0,
    cellClasses: [],
    customBorders: []
  };
}

export function createCaseWorkbookState() {
  const mainSheetId = createWorkbookSheetId();
  const rawSheetId = createWorkbookSheetId();

  return {
    version: PROJECT_WORKBOOK_VERSION,
    activeSheetId: mainSheetId,
    sheets: [
      { id: mainSheetId, name: CASE_MAIN_SHEET_NAME, state: createCaseSheetState({ includeHeaders: true }) },
      { id: rawSheetId, name: CASE_RAW_SHEET_NAME, state: createCaseSheetState({ includeHeaders: true }) }
    ]
  };
}

export function createDefaultCaseUiState() {
  const workflowGroups = CASE_WORKFLOW_GROUPS.reduce((state, group) => {
    state[group.id] = group.id === 'intake';
    return state;
  }, {});

  const deliverableGroups = CASE_DELIVERABLE_GROUPS.reduce((state, group) => {
    state[group.id] = false;
    return state;
  }, {});

  return {
    foldouts: {
      workflowGroups,
      deliverableGroups
    }
  };
}

export function normalizeBooleanMap(source = {}, defaults = {}) {
  const normalized = { ...defaults };

  if (!source || typeof source !== 'object') {
    return normalized;
  }

  Object.keys(source).forEach((key) => {
    normalized[key] = !!source[key];
  });

  return normalized;
}

export function normalizeCaseUiState(rawState = {}) {
  const defaults = createDefaultCaseUiState();
  const asPlainObject = value => value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const state = asPlainObject(rawState);
  const foldouts = asPlainObject(state.foldouts);
  const workflowFoldouts = {
    ...asPlainObject(state.workflowGroupOpenById),
    ...asPlainObject(foldouts.workflow),
    ...asPlainObject(foldouts.workflowGroups)
  };
  const deliverableFoldouts = {
    ...asPlainObject(state.deliverableGroupOpenById),
    ...asPlainObject(foldouts.deliverables),
    ...asPlainObject(foldouts.deliverableGroups)
  };

  return {
    ...defaults,
    ...state,
    foldouts: {
      workflowGroups: normalizeBooleanMap(workflowFoldouts, defaults.foldouts.workflowGroups),
      deliverableGroups: normalizeBooleanMap(deliverableFoldouts, defaults.foldouts.deliverableGroups)
    }
  };
}

export function createEmptyCaseSummary() {
  return {
    mainEntries: 0,
    mainHashRows: 0,
    startingMainTransactions: 0,
    startingMainTrashRows: 0,
    startingMainDetectionMode: 'empty',
    rawEntries: 0,
    rawHashRows: 0,
    rawDataSets: 0,
    rawMatchedMainTransactions: 0,
    rawCoveredMainTransactions: 0,
    rawMissingMainTransactions: 0,
    rawMismatchedMainTransactions: 0,
    saplingPreparedMainTransactions: 0,
    saplingUnprocessedMainTransactions: 0,
    saplingReprocessAvailableTransactions: 0,
    qualifiedPathOutputs: 0,
    pathEndingOutputs: 0,
    graftReadyPathEndings: 0,
    outputRows: 0,
    destinationAddressCount: 0,
    caseAddressCount: 0,
    attributedAddressCount: 0,
    missingAttributionAddressCount: 0,
    attributionApplicableRowCount: 0,
    attributionFilledRowCount: 0,
    missingAttributionOccurrences: 0,
    missingDates: 0,
    invalidDates: 0,
    missingNetworks: 0,
    missingPathLabels: 0,
    missingAttribution: 0,
    duplicateHashCount: 0,
    duplicateHashGroupCount: 0,
    duplicateHashEntryCount: 0,
    duplicateHashEligibleUpdateCount: 0,
    staleDuplicateHashNoteCount: 0,
    noteHashRewriteEligibleCount: 0,
    originalNotesBackupRows: 0,
    refreshedAtUtc: ''
  };
}

export function createDefaultCaseSupportState() {
  return {
    version: CASE_SUPPORT_STATE_VERSION,
    caseDetails: {
      caseName: '',
      caseId: '',
      caseType: ''
    },
    caseDataRevision: 0,
    dirty: false,
    dirtySinceUtc: '',
    lastSavedRevision: 0,
    lastSavedUtc: '',
    lastValidationRevision: null,
    lastValidationUtc: '',
    lastMutation: null,
    checkResultsById: {},
    emptyCaseStarterNudge: {
      schemaVersion: 1,
      dismissedForCase: false,
      dismissedAtUtc: '',
      dismissedReason: ''
    },
    workflowState: {
      manualCompleteByTag: {},
      signalCompleteByCode: {},
      toolRuns: [],
      tool68PreparedByMainKey: {},
      addressColorRun: null,
      entityReview: null,
      inconsistencyReview: null,
      depositHotWalletReview: null,
      depositPatternReview: null,
      knownEntityCasingReview: null,
      duplicateHashLabelerReview: null,
      noteHashPathLabelRewriteReview: null,
      whatWentWhereUsdReferenceReview: null,
      addressLegendReview: null,
      suspiciousAddressTableReview: null,
      exchangeAddressTableReview: null,
      dormantAddressDeliverablesReview: null,
      whatWentWhereReview: null,
      caseConclusionReview: null,
      reportBodyReview: null,
      addendumPathEndingReview: null,
      addendumRecipientEntityReview: null,
      addendumBuildReview: null,
      fullReportPackageReview: null,
      wayFinderHandoff: createEmptyWayFinderHandoffState()
    },
    uiState: createDefaultCaseUiState(),
    deliverableStateById: {},
    outputReview: {
      status: 'empty',
      title: '',
      detail: '',
      generatedAtUtc: '',
      sourceActionId: '',
      sourceDeliverableId: '',
      target: '',
      reviewedAtUtc: ''
    }
  };
}


export function sanitizeCaseCheckResultDetails(value, depth = 0) {
  if (value == null) return value;
  const valueType = typeof value;
  if (valueType === 'string') return value.length > 600 ? `${value.slice(0, 600)}…` : value;
  if (valueType === 'number' || valueType === 'boolean') return value;
  if (Array.isArray(value)) {
    return { type: 'array', count: value.length };
  }
  if (valueType !== 'object') return String(value);

  const entries = Object.entries(value);
  if (depth >= 2) {
    return {
      type: 'object',
      keyCount: entries.length,
      keys: entries.slice(0, 24).map(([key]) => key)
    };
  }

  return entries.slice(0, 80).reduce((summary, [key, child]) => {
    const childType = child == null ? 'null' : Array.isArray(child) ? 'array' : typeof child;
    if (childType === 'array') {
      summary[key] = { type: 'array', count: child.length };
    } else if (childType === 'object') {
      summary[key] = sanitizeCaseCheckResultDetails(child, depth + 1);
    } else {
      summary[key] = sanitizeCaseCheckResultDetails(child, depth + 1);
    }
    return summary;
  }, entries.length > 80 ? { truncatedKeyCount: entries.length - 80 } : {});
}

export function sanitizeCaseCheckResult(result = {}) {
  if (!result || typeof result !== 'object') return result;
  const sanitized = {
    ...result,
    details: sanitizeCaseCheckResultDetails(result.details || {})
  };
  return sanitized;
}

export function sanitizeCaseCheckResultsById(resultsById = {}) {
  if (!resultsById || typeof resultsById !== 'object') return {};
  return Object.entries(resultsById).reduce((normalized, [id, result]) => {
    if (!id || !result || typeof result !== 'object') return normalized;
    normalized[id] = sanitizeCaseCheckResult(result);
    return normalized;
  }, {});
}

export function createStorageSafeCaseSupportState(state = {}) {
  const safeState = state && typeof state === 'object' ? state : createDefaultCaseSupportState();
  return {
    ...safeState,
    checkResultsById: sanitizeCaseCheckResultsById(safeState.checkResultsById || {})
  };
}

export function normalizeCaseSupportState(rawState = {}) {
  const defaults = createDefaultCaseSupportState();
  const state = rawState && typeof rawState === 'object' ? rawState : {};
  const workflowState = state.workflowState && typeof state.workflowState === 'object' ? state.workflowState : {};
  const outputReview = state.outputReview && typeof state.outputReview === 'object' ? state.outputReview : {};

  return {
    ...defaults,
    ...state,
    version: CASE_SUPPORT_STATE_VERSION,
    caseDetails: {
      ...defaults.caseDetails,
      ...(state.caseDetails && typeof state.caseDetails === 'object' ? state.caseDetails : {})
    },
    caseDataRevision: Number.isFinite(Number(state.caseDataRevision)) ? Number(state.caseDataRevision) : 0,
    emptyCaseStarterNudge: {
      ...defaults.emptyCaseStarterNudge,
      ...(state.emptyCaseStarterNudge && typeof state.emptyCaseStarterNudge === 'object' ? state.emptyCaseStarterNudge : {}),
      schemaVersion: 1,
      dismissedForCase: !!(state.emptyCaseStarterNudge && typeof state.emptyCaseStarterNudge === 'object' ? state.emptyCaseStarterNudge.dismissedForCase : false)
    },
    dirty: !!state.dirty,
    lastValidationRevision: Number.isFinite(Number(state.lastValidationRevision)) ? Number(state.lastValidationRevision) : null,
    checkResultsById: sanitizeCaseCheckResultsById(state.checkResultsById && typeof state.checkResultsById === 'object' ? state.checkResultsById : {}),
    workflowState: {
      manualCompleteByTag: workflowState.manualCompleteByTag && typeof workflowState.manualCompleteByTag === 'object' ? workflowState.manualCompleteByTag : {},
      signalCompleteByCode: workflowState.signalCompleteByCode && typeof workflowState.signalCompleteByCode === 'object' ? workflowState.signalCompleteByCode : {},
      toolRuns: Array.isArray(workflowState.toolRuns) ? workflowState.toolRuns.slice(-80) : [],
      tool68PreparedByMainKey: workflowState.tool68PreparedByMainKey && typeof workflowState.tool68PreparedByMainKey === 'object' ? workflowState.tool68PreparedByMainKey : {},
      addressColorRun: workflowState.addressColorRun && typeof workflowState.addressColorRun === 'object' ? { ...workflowState.addressColorRun } : null,
      entityReview: workflowState.entityReview && typeof workflowState.entityReview === 'object' ? { ...workflowState.entityReview } : null,
      inconsistencyReview: workflowState.inconsistencyReview && typeof workflowState.inconsistencyReview === 'object' ? {
        ...workflowState.inconsistencyReview,
        resolvedCardKeys: workflowState.inconsistencyReview.resolvedCardKeys && typeof workflowState.inconsistencyReview.resolvedCardKeys === 'object'
          ? { ...workflowState.inconsistencyReview.resolvedCardKeys }
          : {}
      } : null,
      depositHotWalletReview: workflowState.depositHotWalletReview && typeof workflowState.depositHotWalletReview === 'object'
        ? { ...workflowState.depositHotWalletReview }
        : null,
      depositPatternReview: workflowState.depositPatternReview && typeof workflowState.depositPatternReview === 'object'
        ? {
          ...workflowState.depositPatternReview,
          decisionsByKey: workflowState.depositPatternReview.decisionsByKey && typeof workflowState.depositPatternReview.decisionsByKey === 'object'
            ? { ...workflowState.depositPatternReview.decisionsByKey }
            : {}
        }
        : null,
      knownEntityCasingReview: workflowState.knownEntityCasingReview && typeof workflowState.knownEntityCasingReview === 'object'
        ? {
          ...workflowState.knownEntityCasingReview,
          decisionsByKey: workflowState.knownEntityCasingReview.decisionsByKey && typeof workflowState.knownEntityCasingReview.decisionsByKey === 'object'
            ? { ...workflowState.knownEntityCasingReview.decisionsByKey }
            : {}
        }
        : null,
      duplicateHashLabelerReview: workflowState.duplicateHashLabelerReview && typeof workflowState.duplicateHashLabelerReview === 'object'
        ? { ...workflowState.duplicateHashLabelerReview }
        : null,
      noteHashPathLabelRewriteReview: workflowState.noteHashPathLabelRewriteReview && typeof workflowState.noteHashPathLabelRewriteReview === 'object'
        ? {
          ...workflowState.noteHashPathLabelRewriteReview,
          backup: workflowState.noteHashPathLabelRewriteReview.backup && typeof workflowState.noteHashPathLabelRewriteReview.backup === 'object'
            ? {
              ...workflowState.noteHashPathLabelRewriteReview.backup,
              rows: Array.isArray(workflowState.noteHashPathLabelRewriteReview.backup.rows)
                ? workflowState.noteHashPathLabelRewriteReview.backup.rows.slice()
                : []
            }
            : null
        }
        : null,
      whatWentWhereUsdReferenceReview: workflowState.whatWentWhereUsdReferenceReview && typeof workflowState.whatWentWhereUsdReferenceReview === 'object'
        ? { ...workflowState.whatWentWhereUsdReferenceReview }
        : null,
      addressLegendReview: workflowState.addressLegendReview && typeof workflowState.addressLegendReview === 'object'
        ? {
          ...workflowState.addressLegendReview,
          entries: Array.isArray(workflowState.addressLegendReview.entries)
            ? workflowState.addressLegendReview.entries.map(entry => ({ ...entry }))
            : []
        }
        : null,
      suspiciousAddressTableReview: workflowState.suspiciousAddressTableReview && typeof workflowState.suspiciousAddressTableReview === 'object'
        ? { ...workflowState.suspiciousAddressTableReview }
        : null,
      exchangeAddressTableReview: workflowState.exchangeAddressTableReview && typeof workflowState.exchangeAddressTableReview === 'object'
        ? {
          ...workflowState.exchangeAddressTableReview,
          exclusionsByAddressKey: workflowState.exchangeAddressTableReview.exclusionsByAddressKey && typeof workflowState.exchangeAddressTableReview.exclusionsByAddressKey === 'object'
            ? { ...workflowState.exchangeAddressTableReview.exclusionsByAddressKey }
            : {}
        }
        : null,
      dormantAddressDeliverablesReview: workflowState.dormantAddressDeliverablesReview && typeof workflowState.dormantAddressDeliverablesReview === 'object'
        ? { ...workflowState.dormantAddressDeliverablesReview }
        : null,
      whatWentWhereReview: workflowState.whatWentWhereReview && typeof workflowState.whatWentWhereReview === 'object'
        ? { ...workflowState.whatWentWhereReview }
        : null,
      caseConclusionReview: workflowState.caseConclusionReview && typeof workflowState.caseConclusionReview === 'object'
        ? { ...workflowState.caseConclusionReview }
        : null,
      reportBodyReview: workflowState.reportBodyReview && typeof workflowState.reportBodyReview === 'object'
        ? { ...workflowState.reportBodyReview }
        : null,
      addendumPathEndingReview: workflowState.addendumPathEndingReview && typeof workflowState.addendumPathEndingReview === 'object'
        ? { ...workflowState.addendumPathEndingReview }
        : null,
      addendumRecipientEntityReview: workflowState.addendumRecipientEntityReview && typeof workflowState.addendumRecipientEntityReview === 'object'
        ? {
          ...workflowState.addendumRecipientEntityReview,
          decisionsByReviewKey: workflowState.addendumRecipientEntityReview.decisionsByReviewKey && typeof workflowState.addendumRecipientEntityReview.decisionsByReviewKey === 'object'
            ? { ...workflowState.addendumRecipientEntityReview.decisionsByReviewKey }
            : {},
          decisionsByKey: workflowState.addendumRecipientEntityReview.decisionsByKey && typeof workflowState.addendumRecipientEntityReview.decisionsByKey === 'object'
            ? { ...workflowState.addendumRecipientEntityReview.decisionsByKey }
            : {},
          resolvedRows: Array.isArray(workflowState.addendumRecipientEntityReview.resolvedRows)
            ? workflowState.addendumRecipientEntityReview.resolvedRows.map(row => ({ ...row }))
            : []
        }
        : null,
      addendumBuildReview: workflowState.addendumBuildReview && typeof workflowState.addendumBuildReview === 'object'
        ? { ...workflowState.addendumBuildReview }
        : null,
      fullReportPackageReview: workflowState.fullReportPackageReview && typeof workflowState.fullReportPackageReview === 'object'
        ? { ...workflowState.fullReportPackageReview }
        : null,
      wayFinderHandoff: workflowState.wayFinderHandoff && typeof workflowState.wayFinderHandoff === 'object'
        ? createWayFinderHandoffStateForStorage(workflowState.wayFinderHandoff)
        : createEmptyWayFinderHandoffState()
    },
    uiState: normalizeCaseUiState(state.uiState || {}),
    deliverableStateById: state.deliverableStateById && typeof state.deliverableStateById === 'object' ? state.deliverableStateById : {},
    outputReview: {
      ...defaults.outputReview,
      ...outputReview
    }
  };
}
